/**
 * Cart line "remove" handler.
 *
 * Why this exists:
 *   The theme's cart-item markup uses <cart-remove-button> wrapping an
 *   <a href="/cart/change?line=X&quantity=0"> as the line-remove control,
 *   but no JS ever wires it up. Without a handler, the anchor performs a
 *   full-page navigation that closes the drawer.
 *
 *   In the drawer context, `line` is also empty (cart.ajax.liquid renders
 *   cart-item without a `line_index` parameter), so the anchor wouldn't
 *   even remove the right line — it would just bounce to /cart.
 *
 *   We CANNOT reuse the theme's existing `cart:quantity` event because
 *   theme.CartForm.quantityChanged early-returns on falsy qty:
 *
 *       if (!key || !qty) { return; }
 *
 *   `!0` is true, so dispatching with quantity 0 is silently dropped. We
 *   call theme.cart.changeItem(key, 0) directly instead and trigger a
 *   rebuild via the existing `cart:build` listener.
 *
 * Strategy:
 *   - Document-level click delegation in capture phase so we run before
 *     any other click listeners and reliably preventDefault on the inner
 *     anchor before the browser starts navigating.
 *   - POST quantity 0 to /cart/change.js via theme.cart.changeItem.
 *   - On success, dispatch `cart:build` so theme.CartForm rebuilds the
 *     drawer (re-renders items, updates subtotal, count bubble, savings,
 *     discounts) and `cart:updated` for any other listeners (sticky bar,
 *     analytics, etc.).
 *
 * Bundle items rendered by loop_bundle.js also use <cart-remove-button>
 * but with their own inline onclick="removeBundleLoop(...)" and no
 * .cart__item parent. We bail out for those so their behavior is intact.
 */
(function () {
  'use strict';

  function rebuildCart(parsedCart) {
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart: parsedCart }
    }));
  }

  function removeLine(key, cartItem, innerButton) {
    function clearLoading() {
      cartItem.classList.remove('is-loading');
      if (innerButton) innerButton.classList.remove('btn--loading');
    }

    if (!window.theme || !window.theme.cart || typeof window.theme.cart.changeItem !== 'function') {
      // Theme JS not available; let the browser do the default navigation.
      clearLoading();
      return;
    }

    window.theme.cart.changeItem(key, 0)
      .then(function (raw) {
        var parsed;
        try { parsed = JSON.parse(raw); } catch (_) { parsed = null; }

        if (parsed && parsed.status === 422) {
          alert(parsed.message || 'Could not remove item.');
          clearLoading();
          return;
        }

        // Success — `cart:build` will re-render this row entirely, so
        // the loading classes are cleaned up implicitly with the DOM.
        rebuildCart(parsed);
      })
      .catch(function (err) {
        clearLoading();
        // eslint-disable-next-line no-console
        console.error('[cart-remove-button] failed to remove line:', err);
      });
  }

  function handleCartRemoveClick(event) {
    var button = event.target.closest('cart-remove-button');
    if (!button) return;

    var cartItem = button.closest('.cart__item');
    if (!cartItem) {
      // Loop-bundle remove button — let its own inline onclick run.
      return;
    }

    // Ignore double-clicks while a previous remove is still in flight.
    if (cartItem.classList.contains('is-loading')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    var key = cartItem.dataset.key;
    if (!key) return;

    // Visual loading state:
    //   - dim/disable the whole row (`is-loading` on .cart__item)
    //   - swap the trash icon for a spinner on the inner anchor using
    //     the theme's standard `.btn--loading` style (overridden in
    //     cart-drawer.liquid to fit the small icon-button footprint).
    cartItem.classList.add('is-loading');
    var innerButton = button.querySelector('.button');
    if (innerButton) innerButton.classList.add('btn--loading');

    removeLine(key, cartItem, innerButton);
  }

  // Capture phase guarantees we run before any other click handlers and
  // before the browser starts the default link navigation.
  document.addEventListener('click', handleCartRemoveClick, true);

  // Register as a Web Component for semantics (no behavior here — the
  // delegated click handler above does all the work).
  if (window.customElements && !customElements.get('cart-remove-button')) {
    try {
      customElements.define('cart-remove-button', class extends HTMLElement {});
    } catch (_) {
      // Already defined elsewhere — safe to ignore.
    }
  }
})();
